import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Button, createStyles, Group, Stack, Tabs, Text, Title} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import CanvasPreviewFrame from 'canvas-core/preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl from 'canvas-core/preview/useCanvasExpandedPreviewUrl';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import AgentFactoryAccessModal from 'llm/chat-library/factories/AgentFactoryAccessModal';
import AgentFactoryAgents from 'llm/chat-library/factories/AgentFactoryAgents';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  AgentFactoryQuery as AgentFactoryQueryData,
  AgentFactoryQueryVariables,
} from './__generated/AgentFactoryQuery';

enum AgentFactoryTab {
  AGENTS = 'AGENTS',
  CANVAS = 'CANVAS',
}

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing.md,
    height: '100%',
    minHeight: 0,
    padding: theme.spacing.xl,
  },
  tabsRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  canvasPanel: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    paddingTop: theme.spacing.md,
  },
  agentsPanel: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing.md,
  },
}));

// The routine fields the agents roster needs to render a row (name, schedule, status) are
// selected directly on `coordinatorAgent`/`agents.agent` here rather than via a separate
// routinesByIds lookup: both are already typed Routine references on AgentFactory, so there is no
// separate id-resolution step to save a round trip for.
export const AGENT_FACTORY_QUERY = gql`
  query AgentFactoryQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentFactory(id: $id) {
      __typename
      ... on AgentFactoryNode {
        id
        name
        description
        createdAt
        creatorName
        coordinatorAgent {
          id
          name
          triggerType
          triggerConfig
          status
        }
        agents {
          agent {
            id
            name
            triggerType
            triggerConfig
            status
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * The canvas backing an agent factory, filling whatever height its container gives it. Reuses
 * useCanvasExpandedPreviewUrl and CanvasPreviewFrame -- the same signed-URL fetch and
 * iframe/sandbox setup chat-embedded canvases use (see ChatCanvasArtifactView) -- but renders only
 * the canvas contents, without any of that view's card chrome (icon tile, version badge, refresh
 * button), since this page already has its own header.
 *
 * Fetches by `agentFactoryId` so the render endpoint can authorize on the factory itself, letting
 * collaborators without direct canvas-version access view it too. Any render failure (no canvas
 * configured, missing asset, etc.) surfaces as the same error banner below.
 */
function AgentFactoryCanvas({agentFactoryId}: {agentFactoryId: string}): JSX.Element {
  const {signedUrl, isLoading, errorMessage} = useCanvasExpandedPreviewUrl({agentFactoryId});

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (errorMessage != null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text={errorMessage}
        {...getTestProps({component: 'AgentFactoryPage', element: 'noCanvasBanner'})}
      />
    );
  }

  if (signedUrl == null) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load the canvas." />;
  }

  return <CanvasPreviewFrame signedUrl={signedUrl} />;
}

/**
 * Detail view for a single Agent Factory, rendered as its own workspace tab (see BufferUtil and
 * EditorRoutes.agentFactoryEditor) and addressable at its own URL (see AIRoutes): the factory's name
 * and description, a "Manage access" button that opens AgentFactoryAccessModal, and tabs for the
 * canvas currently backing it and the agents provisioned for it. The Manage access button is shown
 * to any viewer who can load this page -- like RoutineAccessTab, the modal it opens renders
 * read-only for anyone without canManageCollaborators, rather than being hidden outright.
 */
export default function AgentFactoryPage({agentFactoryId}: {agentFactoryId: string}): JSX.Element {
  const {classes} = useStyles();
  const [isAccessModalOpen, {open: openAccessModal, close: closeAccessModal}] = useDisclosure(false);
  const queryResult = useBenchlingQuery<AgentFactoryQueryData, AgentFactoryQueryVariables>(
    AGENT_FACTORY_QUERY,
    {variables: {id: agentFactoryId}}
  );

  if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    queryResult.type === QueryResultType.ERROR ||
    queryResult.data.agentFactory == null ||
    isInaccessible(queryResult.data.agentFactory)
  ) {
    return (
      <Stack spacing="md" className={classes.container}>
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="This factory could not be found, or you do not have access to it."
        />
      </Stack>
    );
  }

  const {agentFactory} = queryResult.data;

  return (
    <div className={classes.container} {...getTestProps({component: 'AgentFactoryPage', element: 'root'})}>
      <Group position="apart" align="flex-start" noWrap={true}>
        <Stack spacing={4}>
          <Title order={3}>{agentFactory.name}</Title>
          <Text c="dimmed">{agentFactory.description || 'No description yet.'}</Text>
        </Stack>
        <Button
          variant="secondary"
          onClick={openAccessModal}
          {...getTestProps({component: 'AgentFactoryPage', element: 'manageAccessButton'})}
        >
          Manage access
        </Button>
      </Group>
      <Tabs defaultValue={AgentFactoryTab.CANVAS} keepMounted={false} className={classes.tabsRoot}>
        <Tabs.List>
          <Tabs.Tab
            value={AgentFactoryTab.CANVAS}
            {...getTestProps({component: 'AgentFactoryPage', element: 'tab', key: 'Overview'})}
            data-amp-safe={false}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value={AgentFactoryTab.AGENTS}
            {...getTestProps({component: 'AgentFactoryPage', element: 'tab', key: 'Agents'})}
            data-amp-safe={false}
          >
            Agents
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value={AgentFactoryTab.CANVAS} className={classes.canvasPanel}>
          <AgentFactoryCanvas agentFactoryId={agentFactoryId} />
        </Tabs.Panel>

        <Tabs.Panel value={AgentFactoryTab.AGENTS} className={classes.agentsPanel}>
          <AgentFactoryAgents coordinatorAgent={agentFactory.coordinatorAgent} agents={agentFactory.agents} />
        </Tabs.Panel>
      </Tabs>
      {isAccessModalOpen && (
        <AgentFactoryAccessModal agentFactoryId={agentFactoryId} onRequestClose={closeAccessModal} />
      )}
    </div>
  );
}
